const VoiceService = require('./VoiceService');
const querystring = require('querystring');
const { convertPCMBufferToWAV } = require('../Util/Audio');

class ElevenLabsVoiceService extends VoiceService {
	serviceUrl = 'https://api.elevenlabs.io/v1/text-to-speech/';
	apiKey;
	audioFormat;
	defaultVoice = '7CnU7YEtwyL4ggGG48pr';

	defaultVoiceSettings = {
		speed: 1,
		style: 0
	};

	// Config prefix: VOICE_<SERVICE>_<CONFIG PROPERTY>
	/* ENV Config:
		API_KEY
	*/
	constructor() {
		super( 'elevenlabs' );
	}

	static voiceOptions() {
		return [
			{
				id: '7CnU7YEtwyL4ggGG48pr',
				name: 'Skylar'
			},
			{
				id: 'avlMXl8FVWLdTb8Bouvi',
				name: 'Buddy'
			}
		];
	}

	/**
	 * Get audio format enum from string
	 * Maps Azure-style format strings to ElevenLabs format strings
	 */
	getAudioFormat(formatString) {
		const format = formatString || this.audioFormat;

		// Map common format strings to ElevenLabs API format strings
		const formatMap = {
			'audio-24khz-48kbitrate-mono-mp3': 'mp3_22050_32',
			'audio-48khz-96kbitrate-mono-mp3': 'mp3_44100_96',
			'audio-44khz-128kbitrate-mono-mp3': 'mp3_44100_128',
			'riff-16khz-16bit-mono-pcm': 'pcm_16000',
			'riff-24khz-16bit-mono-pcm': 'pcm_22050',
		};

		// Default to MP3 format that works well with web browsers
		return formatMap[format] || 'mp3_44100_128';
	}

	async synthesizeSpeech( text, withTimestamps ) {

		//console.log( 'Getting audio response', url, text );
		const response = await this.apiRequest( text, withTimestamps );

		if (!response) {
			throw new Error('ElevenLabs API returned no response');
		}

		if (!response.audio_base64) {
			throw new Error('ElevenLabs API response missing audio data');
		}

		// Convert PCM data to WAV format if needed for client compatibility
		let audioData = response.audio_base64;
		const outputFormat = this.getAudioFormat(this.audioFormat);

		if (outputFormat.startsWith('pcm')) {
			// Convert raw PCM to WAV format with proper headers
			audioData = this.convertPCMToWAV(response.audio_base64, outputFormat);
		}

		const result = {
			audio_base64: audioData,
		};

		if ( withTimestamps && response?.alignment ) {
			result.messagedata = this.processCharAlignment( response );
		}

		return result;
	}

	/**
	 * Convert raw PCM data to WAV format with proper headers
	 * This allows the client's audioContext.decodeAudioData() to work properly
	 */
	convertPCMToWAV(base64PCMData, format) {
		try {
			// Parse format to get sample rate
			const sampleRate = this.getSampleRateFromFormat(format);

			// Decode base64 PCM data
			const pcmData = Buffer.from(base64PCMData, 'base64');

			// Use the existing utility function to convert PCM buffer to WAV
			const wavBuffer = convertPCMBufferToWAV(pcmData, sampleRate, 16, 1);

			// Return as base64
			return wavBuffer.toString('base64');

		} catch (error) {
			console.error('Failed to convert PCM to WAV:', error);
			// Return original data if conversion fails
			return base64PCMData;
		}
	}

	/**
	 * Extract sample rate from ElevenLabs format string
	 */
	getSampleRateFromFormat(format) {
		const formatMap = {
			'pcm_16000': 16000,
			'pcm_22050': 22050,
			'pcm_24000': 24000,
			'pcm_44100': 44100,
		};

		return formatMap[format] || 22050; // Default to 22050 Hz
	}

	async apiRequest( text, withTimestamps ) {
		let url = this.serviceUrl + this.voiceId;

		if ( withTimestamps ) {
			url += '/with-timestamps';
		}

		const args = {};

		if ( this.audioFormat ) {
			args.output_format = this.getAudioFormat( this.audioFormat );
		}

		url += '?' + querystring.stringify( args );

		const headers = {
			'xi-api-key': this.config.API_KEY,
			'Content-Type': 'application/json'
		};

		return super.apiRequest( { url, data: { text }, headers } );
	}

	processCharAlignment( response ) {
		const chunks = response.normalized_alignment || response.alignment;
		const { characters, character_start_times_seconds, character_end_times_seconds } = chunks;

		return {
			segment: characters,
			start: character_start_times_seconds,
			end: character_end_times_seconds
		};
	}


}

module.exports = ElevenLabsVoiceService;